/**
 * One row of the citizen inbox (F50): who it is from, what it is about, a line
 * of what it says, when it arrived and how, and whether it has been read.
 * Presentation only.
 */
import type { KeyboardEvent } from 'react';
import { Bell, Mail, Smartphone } from '@tonnie/assets';
import { resolveNotificationCopy, useLocale, useTranslation } from '@tonnie/locale';
import type { InboxItem } from '@tonnie/types';
import type { TFunction } from 'i18next';
import { cn } from '@tonnie/ui';

import { isToday, previewLine } from '@tonnie/heroes';
import { SenderAvatar } from './SenderAvatar';

/** Literal keys, so the key-usage gate checks every one against en.json. */
export function inboxChannelLabel(t: TFunction, channel: InboxItem['channel']): string {
  switch (channel) {
    case 'email':
      return t('inbox.channelEmail');
    case 'push':
      return t('inbox.channelPush');
    case 'in_app':
      return t('inbox.channelInApp');
    default: {
      const unreachable: never = channel;
      return unreachable;
    }
  }
}

export function InboxChannelIcon({
  channel,
  className,
}: {
  channel: InboxItem['channel'];
  className?: string;
}) {
  const Icon = channel === 'email' ? Mail : channel === 'push' ? Smartphone : Bell;
  return <Icon className={className} aria-hidden />;
}

export function inboxSenderName(item: InboxItem, platformName: string): string {
  return item.sender.kind === 'company' ? item.sender.name : platformName;
}

/** The message's title as the person should read it, in their language. */
export function inboxSubject(t: TFunction, item: InboxItem): string {
  const content = item.content;
  switch (content.state) {
    case 'copy':
      return content.subject;
    case 'not_retained':
      return t('inbox.notRetainedTitle');
    case 'in_app':
      return resolveNotificationCopy(t, {
        key: content.titleKey ?? undefined,
        values: content.values ?? undefined,
        column: content.title,
      });
    default: {
      const unreachable: never = content;
      return unreachable;
    }
  }
}

/** One line of what the message says — never anything it did not say. */
export function inboxPreview(t: TFunction, item: InboxItem): string {
  const content = item.content;
  switch (content.state) {
    case 'copy':
      return previewLine(content.bodyText);
    case 'not_retained':
      return t('inbox.notRetainedPreview');
    case 'in_app':
      return previewLine(
        resolveNotificationCopy(t, {
          key: content.bodyKey ?? undefined,
          values: content.values ?? undefined,
          column: content.body,
        })
      );
    default: {
      const unreachable: never = content;
      return unreachable;
    }
  }
}

interface InboxListItemProps {
  item: InboxItem;
  selected: boolean;
  now: Date;
  onOpen: (item: InboxItem) => void;
  /** The list's arrow-key movement between rows. */
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

export function InboxListItem({ item, selected, now, onOpen, onKeyDown }: InboxListItemProps) {
  const { t } = useTranslation();
  const { formatTime, formatDate } = useLocale();
  const unread = !item.readAt;
  // Mail-client convention: a time for today, a short date before that.
  const when = isToday(item.sentAt, now)
    ? formatTime(item.sentAt, { hour: 'numeric', minute: '2-digit' })
    : formatDate(item.sentAt, { day: 'numeric', month: 'short' });

  return (
    <button
      type="button"
      data-testid="inbox-item"
      data-inbox-id={item.id}
      data-inbox-read={unread ? 'false' : 'true'}
      data-inbox-channel={item.channel}
      data-inbox-content={item.content.state}
      data-inbox-notification-id={
        item.content.state === 'in_app' ? item.content.notificationId : undefined
      }
      aria-current={selected ? 'true' : undefined}
      onClick={() => onOpen(item)}
      onKeyDown={onKeyDown}
      className={cn(
        'group relative flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        selected ? 'bg-primary/10 dark:bg-primary/15' : 'hover:bg-muted/60'
      )}
    >
      <SenderAvatar sender={item.sender} />

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'
            )}
          >
            {inboxSenderName(item, t('inbox.fromPlatform'))}
          </span>
          <time
            dateTime={item.sentAt}
            className={cn(
              'shrink-0 text-xs tabular-nums',
              unread ? 'font-medium text-primary-accessible' : 'text-muted-foreground'
            )}
          >
            {when}
          </time>
        </span>

        <span
          className={cn(
            'mt-0.5 block truncate text-sm',
            unread ? 'font-semibold text-foreground' : 'text-foreground/80'
          )}
        >
          {inboxSubject(t, item)}
        </span>

        <span className="mt-1 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {inboxPreview(t, item)}
          </span>
          <span
            className="flex shrink-0 items-center gap-1 text-2xs text-muted-foreground"
            title={inboxChannelLabel(t, item.channel)}
          >
            <InboxChannelIcon channel={item.channel} className="h-3.5 w-3.5" />
            <span className="sr-only">{inboxChannelLabel(t, item.channel)}</span>
          </span>
        </span>
      </span>

      {unread && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />}
      {unread && <span className="sr-only">{`, ${t('inbox.unreadLabel')}`}</span>}
    </button>
  );
}
